import {gql, useMutation} from '@apollo/client'
import {Icon, List, Spinner, Typography} from 'nf-ui'
import SvgDownload from 'nf-ui/Icons/Download'
import React, {useState} from 'react'
import {useAlert} from 'react-alert'
import {useQueryListCategories} from '~/apollo/queries/ListCategories'
import {PanelLayout} from '~/components/PanelLayout'
import {PanelHeader} from '~/components/PanelNavigator'
import {useOrganisationIdStr} from '~/components/useOrganisationIdStr'
import {PANEL_WIDTH} from '~/constants'
import {HomeItemType} from '~/globalTypes'
import {getGraphQLErrorMessage} from '~/util'
import {ExportDirectoryPdf, ExportDirectoryPdfVariables} from './__types__/ExportDirectoryPdf'

const EXPORT_DIRECTORY_PDF = gql`
    mutation ExportDirectoryPdf($organisationIdStr: String!, $categoryIdStr: String!) {
        exportDirectoryPdf(organisationIdStr: $organisationIdStr, categoryIdStr: $categoryIdStr)
    }
`

type ExportPdfCategoryPanelProps = {
    onDone: () => void
}

export const ExportPdfCategoryPanel: React.FC<ExportPdfCategoryPanelProps> = ({onDone}) => {
    const alert = useAlert()
    const organisationIdStr = useOrganisationIdStr()
    const [{data, loading: categoriesLoading}] = useQueryListCategories(organisationIdStr)
    const [exportingCategoryIdStr, setExportingCategoryIdStr] = useState<string | null>(null)

    const [exportDirectoryPdf] = useMutation<ExportDirectoryPdf, ExportDirectoryPdfVariables>(EXPORT_DIRECTORY_PDF, {
        onError: (error) => {
            alert.error(getGraphQLErrorMessage(error))
            throw error
        },
    })

    const categories =
        data?.organisation.homeItems
            .filter((item) => item.type === HomeItemType.CATEGORY && item.sourceIdStr)
            .map((item) => ({
                idStr: item.sourceIdStr!,
                label: item.label,
            })) || []

    return (
        <PanelLayout width={PANEL_WIDTH}>
            <PanelHeader panel="exportPdf">Export PDF</PanelHeader>
            {categoriesLoading ? (
                <div style={{display: 'flex', justifyContent: 'center', padding: 24}}>
                    <Spinner />
                </div>
            ) : categories.length === 0 ? (
                <Typography.Paragraph>No categories available to export.</Typography.Paragraph>
            ) : (
                <List
                    rows={categories}
                    width={PANEL_WIDTH}
                    onClick={async (category) => {
                        if (exportingCategoryIdStr) return
                        setExportingCategoryIdStr(category.idStr)
                        try {
                            const result = await exportDirectoryPdf({
                                variables: {
                                    organisationIdStr,
                                    categoryIdStr: category.idStr,
                                },
                            })
                            const url = result.data?.exportDirectoryPdf
                            if (url) window.open(url, '_blank')
                            onDone()
                        } finally {
                            setExportingCategoryIdStr(null)
                        }
                    }}
                    renderRow={(category) => (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                height: '100%',
                            }}
                        >
                            {category.label}
                            {exportingCategoryIdStr === category.idStr ? (
                                <Spinner size={16} />
                            ) : (
                                <Icon icon={SvgDownload} />
                            )}
                        </div>
                    )}
                />
            )}
        </PanelLayout>
    )
}
